<template>
  <div class="flex flex-col gap-[32px] mt-20">
    <!-- Heading Section -->
    <PageHeading
      title="Personal Information"
      subtitle="Please provide your personal information below."
    />

    <!-- Form Section -->
    <div class="max-w-lg flex flex-col gap-[20px] h-[calc(100vh-356px)] overflow-y-auto pr-4">
      <!-- Profile Image Upload -->
      <FormLogoUpload
        v-model="form.image"
        label="Upload Profile Image"
        required
        :placeholder="imageDisplay"
        :errorMessage="shouldShowError(v$.image) ? v$.image.$errors[0].$message : ''"
      />

      <!-- User Details -->
      <FormInput
        label="Full Name"
        v-model="form.name"
        required
        placeholder="Enter your full name"
        :errorMessage="shouldShowError(v$.name) ? v$.name.$errors[0].$message : ''"
      />

      <!-- Email -->
      <FormEmailInput v-model="form.email" :readonly="true" />

      <!-- Position -->
      <FormInput
        label="Position"
        v-model="form.position"
        required
        placeholder="Enter position"
        :errorMessage="shouldShowError(v$.position) ? v$.position.$errors[0].$message : ''"
      />

      <!-- Phone Number -->
      <FormPhoneInput
        label="Phone Number"
        v-model="form.phone"
        required
        :defaultCountry="form.phoneCountry"
        :errorMessage="shouldShowError(v$.phone) ? v$.phone.$errors[0].$message : ''"
        @validate="handlePhoneValidation"
        @country-changed="handleCountryChange"
        @blur="fieldTouched.phone = true"
      />

      <!-- Submit Button -->
      <div class="w-full h-[48px] flex items-center justify-center">
        <NextButton 
          text="Next" 
          nextRoute="team" 
          :validateForm="true" 
          :v$="v$" 
          :form="form"
          @click="submitForm" 
          ref="submitBtn"
          :disabled="isSendingRequest"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import useVuelidate from "@vuelidate/core";
import { required, helpers } from "@vuelidate/validators";
import defaultImage from "@/assets/upload_logo.svg";
import PageHeading from "@/components/PageHeading.vue";
import FormLogoUpload from "@/components/FormLogoUpload.vue";
import FormInput from "@/components/FormInput.vue";
import FormPhoneInput from "@/components/FormPhoneInput.vue";
import FormEmailInput from "@/components/FormEmailInput.vue";
import NextButton from "@/components/NextButton.vue";
import global from "@/global";
import { useAuthStore } from "@/stores/auth";

const router = useRouter();
const authStore = useAuthStore();
const submitBtn = ref(null);
const isSendingRequest = ref(false);

// Track form submission state
const formSubmitted = ref(false);

// Form Data
const form = ref({
  name: "",
  position: "",
  phone: "",
  image: null,
  phoneCountry: "us",
  isPhoneValid: false,
  phoneValidationObj: null,
  email: "",
  preferred_language: "",
  time_zone: "",
});

// Track whether the phone field has been touched
const fieldTouched = ref({ phone: false });

// Custom phone validator
const isValidPhoneNumber = (value) => {
  if (!value) return false;
  return form.value.isPhoneValid;
};

// Add this function to format phone number
const formatPhoneNumber = (phone, validation) => {
  if (!phone || !validation) return '';
  
  // Remove all non-digit characters except +
  let formattedNumber = phone.replace(/[^\d+]/g, '');
  
  // Ensure it starts with + if it's an international number
  if (validation.countryCode && !formattedNumber.startsWith('+')) {
    formattedNumber = '+' + formattedNumber;
  }
  
  return formattedNumber;
};

const rules = computed(() => ({
  name: { 
    required: helpers.withMessage("Full name is required", required),
    // Add validation for name format if needed
    format: helpers.withMessage("Please enter both first and last name", (value) => {
      return value && value.trim().split(' ').length >= 2;
    })
  },
  position: { required: helpers.withMessage("Position is required", required) },
  phone: {
    required: helpers.withMessage("Phone number is required", required),
    validPhoneNumber: helpers.withMessage(
      "Please enter a valid phone number for the selected country", 
      isValidPhoneNumber
    ),
    format: helpers.withMessage("Phone number must be in a valid format", (value) => {
      if (!value) return true;
      return /^\+?\d{7,15}$/.test(value.replace(/[^\d+]/g, ''));
    })
  },
  image: { required: helpers.withMessage("Profile picture is required", required) },
}));

const v$ = useVuelidate(rules, form);

// Add imageDisplay computed property
const imageDisplay = computed(() => {
  // If form.image is a URL string (from API), return it directly
  if (typeof form.value.image === 'string' && (form.value.image.startsWith('http') || form.value.image.startsWith('/'))) {
    return form.value.image;
  }
  // If form.image exists (File or Data URL from upload), return it
  return form.value.image ? form.value.image : defaultImage;
});

// Function to show error messages
const shouldShowError = (field) => {
  if (field.$path === "phone") {
    return (formSubmitted.value || fieldTouched.value.phone) && field.$error;
  }
  return formSubmitted.value && field.$error;
};

const handlePhoneValidation = (validation) => {
  form.value.isPhoneValid = validation.valid;
  form.value.phoneValidationObj = validation;

  if (validation.valid) {
    // Format the phone number when it's valid
    form.value.phone = formatPhoneNumber(form.value.phone, validation);
  }

  if (formSubmitted.value || fieldTouched.value.phone) {
    v$.value.phone.$touch();
  }
};

const handleCountryChange = (country) => {
  form.value.phoneCountry = country.iso2;
  if ((form.value.phone && fieldTouched.value.phone) || formSubmitted.value) {
    v$.value.phone.$touch();
  }
};

// Helper function to convert data URL to Blob
const dataURLtoBlob = (dataURL) => {
  const arr = dataURL.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  
  return new Blob([u8arr], { type: mime });
};

// Map API data to form fields
const mapApiDataToForm = (apiData) => {
  // Use the combined name from the API
  form.value.name = apiData.name || '';
  form.value.position = apiData.position || '';
  form.value.email = apiData.email || '';
  form.value.preferred_language = apiData.preferred_language || '';
  form.value.time_zone = apiData.time_zone || '';
  
  // Handle phone number
  if (apiData.phone) {
    form.value.phone = apiData.phone;
    if (apiData.phone.startsWith('+')) {
      form.value.phoneCountry = 'us';
    }
  }
  
  // Handle profile image - check both logo_url and logo fields
  if (apiData.logo_url || apiData.logo) {
    form.value.image = apiData.logo_url || apiData.logo;
  }

};

// Map form data to API format
const mapFormToApiData = () => {
  const formData = new FormData();
  
  // Split the full name into first and last name
  const nameParts = form.value.name.trim().split(/\s+/);
  const firstName = nameParts[0] || '';
  const lastName = nameParts.slice(1).join(' ') || '';
  
  formData.append('first_name', firstName);
  formData.append('last_name', lastName);
  formData.append('position', form.value.position);
  
  // Format phone number before sending
  if (form.value.phone && form.value.phoneValidationObj) {
    const formattedPhone = formatPhoneNumber(form.value.phone, form.value.phoneValidationObj);
    formData.append('phone', formattedPhone);
  } else {
    formData.append('phone', form.value.phone || '');
  }
  
  formData.append('preferred_language', form.value.preferred_language);
  formData.append('time_zone', form.value.time_zone);
  
  // Handle image upload
  if (form.value.image instanceof File) {
    formData.append('logo', form.value.image);
  } else if (typeof form.value.image === 'string' && form.value.image.startsWith('data:image')) {
    const blob = dataURLtoBlob(form.value.image);
    formData.append('logo', blob, 'logo.png');
  }
  
  return formData;
};

// Fetch user data on component mount
const fetchUserData = async () => {
  try {
    const res = await fetch(`${global.API_URL}/me`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
        "Content-Type": "application/json"
      }
    });
    
    const response = await res.json();
    console.log("API Response:", response); // Debug log
    
    if (response.status && response.data) {
      mapApiDataToForm(response.data);
    } else {
      console.error("Invalid API response format:", response);
    }
  } catch (err) {
    console.error("Error fetching user data:", err);
  }
};

const submitForm = async () => {
  formSubmitted.value = true;
  const isValid = await v$.value.$validate();

  if (!isValid) {
    console.log("Form has errors", v$.value.$errors);
    return;
  }

  isSendingRequest.value = true;
  if (submitBtn.value) submitBtn.value.disabled = true;

  try {
    const formData = mapFormToApiData();
    
    const res = await fetch(`${global.API_URL}/me`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`
      },
      body: formData
    });

    const response = await res.json();
    
    if (response.status) {
      // Update local form data with the returned data
      if (response.data) {
        mapApiDataToForm(response.data);
      }
      
      // Navigate to next page after successful submission
      setTimeout(() => {
        isSendingRequest.value = false;
        if (submitBtn.value) submitBtn.value.disabled = false;
        router.push({ name: "team" });
      }, 1500);
    } else {
      console.error("API Error:", response.error);
      // You might want to show these errors to the user
      isSendingRequest.value = false;
      if (submitBtn.value) submitBtn.value.disabled = false;
    }
  } catch (err) {
    console.error("API Error:", err);
    isSendingRequest.value = false;
    if (submitBtn.value) submitBtn.value.disabled = false;
  }
};

onMounted(async () => {
  await fetchUserData();
});
</script>
